import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import { requestAccount } from "../../actions";
import AuthLayout from "../auth/AuthLayout";
import { Refusal } from "../workspaces/WorkspaceUi";
import useBranding from "../../utils/branding";

const TOPICS = [
  "Reference Processes (BPMN)",
  "E2E Architecture",
  "Process Driven SAP S/4HANA Cookbook (SAP Activate)",
];
const TOOLS = ["Aeneis", "ARIS", "Signavio"];
const STEPS = ["About you", "Your interests"];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Register() {
  const branding = useBranding();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    position: "",
    phone: "",
  });
  const [topics, setTopics] = useState([]);
  const [tools, setTools] = useState([]);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState(false);

  const set = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }));
  const toggle = (list, setList, item) =>
    setList(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);

  const validateStepOne = () => {
    const next = {};
    if (!emailPattern.test(values.email.trim())) next.email = "Enter your work email, like name@company.com.";
    if (!values.firstName.trim()) next.firstName = "Enter your first name.";
    if (!values.lastName.trim()) next.lastName = "Enter your last name.";
    if (!values.company.trim()) next.company = "Enter your company.";
    if (!values.position.trim()) next.position = "Enter your role.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const next = (e) => {
    e.preventDefault();
    if (validateStepOne()) {
      setServerError("");
      setStep(1);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (topics.length === 0) nextErrors.topics = "Choose at least one topic.";
    if (!consent) nextErrors.consent = "Agree to the processing of your data to request a trial.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setBusy(true);
    setServerError("");
    const res = await requestAccount({
      email: values.email.trim().toLowerCase(),
      company: values.company.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      phone: values.phone.trim(),
      position: values.position.trim(),
      domains: Object.fromEntries(TOOLS.map((t) => [t, tools.includes(t)])),
      topics: Object.fromEntries(TOPICS.map((t) => [t, topics.includes(t)])),
    });
    setBusy(false);
    if (res.success) {
      setDone(true);
      return;
    }
    const msg = res.msg || "We couldn't send your request. Try again.";
    setServerError(msg);
    // Most rejections (e.g. a personal email address) are about step one.
    if (/mail|registered|domain/i.test(msg)) setStep(0);
  };

  if (done) {
    return (
      <AuthLayout>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            bgcolor: "primary.main",
            color: "secondary.main",
            display: "grid",
            placeItems: "center",
            mb: 3,
          }}
        >
          <MarkEmailReadRoundedIcon />
        </Box>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Request received
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 4 }}>
          Thanks, {values.firstName.trim()}. Our team reviews your request and emails your login details to{" "}
          <strong>{values.email.trim().toLowerCase()}</strong>.
        </Typography>
        <Button component={RouterLink} to="/login" variant="contained" size="large" fullWidth>
          Go to log in
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout maxWidth={480}>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Start your free trial
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 2.5 }}>
        14 days of reference processes, free. Use your work email; no credit card needed.
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {serverError && <Refusal sx={{ mb: 3 }}>{serverError}</Refusal>}

      {step === 0 && (
        <Box component="form" onSubmit={next} noValidate>
          <Grid container spacing={1.75}>
            <Grid item xs={12}>
              <TextField
                label="Work email"
                type="email"
                autoComplete="email"
                autoFocus
                value={values.email}
                onChange={set("email")}
                error={Boolean(errors.email)}
                placeholder="name@company.com"
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                autoComplete="given-name"
                value={values.firstName}
                onChange={set("firstName")}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                autoComplete="family-name"
                value={values.lastName}
                onChange={set("lastName")}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company"
                autoComplete="organization"
                value={values.company}
                onChange={set("company")}
                error={Boolean(errors.company)}
                helperText={errors.company}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Role"
                autoComplete="organization-title"
                value={values.position}
                onChange={set("position")}
                error={Boolean(errors.position)}
                helperText={errors.position}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone (optional)"
                type="tel"
                autoComplete="tel"
                value={values.phone}
                onChange={set("phone")}
              />
            </Grid>
            <Grid item xs={12}>
              <LoadingButton type="submit" variant="contained" size="large" fullWidth sx={{ mt: 0.5 }}>
                Continue
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      )}

      {step === 1 && (
        <Box component="form" onSubmit={submit} noValidate>
          <Stack spacing={2.5}>
            <FormControl error={Boolean(errors.topics)} component="fieldset" variant="standard">
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
                What would you like to explore?
              </FormLabel>
              <FormGroup>
                {TOPICS.map((topic) => (
                  <FormControlLabel
                    key={topic}
                    control={
                      <Checkbox checked={topics.includes(topic)} onChange={() => toggle(topics, setTopics, topic)} />
                    }
                    label={topic}
                  />
                ))}
              </FormGroup>
              {errors.topics && <FormHelperText>{errors.topics}</FormHelperText>}
            </FormControl>

            <Box>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Want a personal walkthrough of Ultimate?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Pick the modelling tools you use and we'll tailor the demo.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                {TOOLS.map((tool) => {
                  const selected = tools.includes(tool);
                  return (
                    <Chip
                      key={tool}
                      label={tool}
                      clickable
                      color={selected ? "primary" : "default"}
                      variant={selected ? "filled" : "outlined"}
                      onClick={() => toggle(tools, setTools, tool)}
                      aria-pressed={selected}
                    />
                  );
                })}
              </Stack>
            </Box>

            <FormControl error={Boolean(errors.consent)}>
              <FormControlLabel
                sx={{ alignItems: "flex-start", m: 0 }}
                control={
                  <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} sx={{ mt: -0.75, ml: -1 }} />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I agree that bpExperts processes my personal data to provide the trial, as described in the{" "}
                    <Link href={branding.privacyUrl} target="_blank" rel="noopener noreferrer">
                      privacy statement
                    </Link>
                    . I can withdraw consent and ask for my account to be deleted at any time.
                  </Typography>
                }
              />
              {errors.consent && <FormHelperText>{errors.consent}</FormHelperText>}
            </FormControl>

            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" size="large" onClick={() => setStep(0)} disabled={busy}>
                Back
              </Button>
              <LoadingButton type="submit" variant="contained" size="large" loading={busy} fullWidth>
                Request free trial
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3 }}>
        Already have an account?{" "}
        <Link component={RouterLink} to="/login">
          Log in
        </Link>
      </Typography>
    </AuthLayout>
  );
}
